import { toaster } from '@/components/ui/toaster';
import { useSaveMetaAdsPreferences } from '@/hooks/actions/use-meta-ads-actions';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import { mentionBoostSetupQueryKey } from '@/hooks/queries/use-mention-boost-setup-query';
import { getApiErrorMessage } from '@/libs/utils/error-utils';
import { SetupFieldLabel } from './SetupSection';
import { Box, Button, Input, Text, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface BoostDsaBeneficiaryFormProps {
  mentionId: string;
  // Whatever the brand already recorded, so an edit starts from it rather than
  // from an empty field. Null means nothing is recorded yet: the field then
  // suggests the brand name instead (see the component doc comment).
  beneficiary: string | null;
  payor: string | null;
}

/**
 * Collects the EU Digital Services Act advertiser declaration: who the ads run
 * for, and who pays when that is someone else. Meta refuses to create an ad set
 * targeting the EU without a beneficiary, so this is a launch prerequisite, not
 * an option.
 *
 * It is recorded once at brand level (same store as the default ad account), so
 * a brand fills it on its first boost and never sees this form again.
 *
 * When nothing is recorded yet, the beneficiary field suggests the brand name
 * as an editable starting value (never auto-saved) so most brands can confirm
 * instead of typing from scratch; the trade name we hold can still differ from
 * the legal entity the declaration requires, so the brand must be free to
 * correct it.
 */
export function BoostDsaBeneficiaryForm({
  mentionId,
  beneficiary,
  payor,
}: BoostDsaBeneficiaryFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const savePreferences = useSaveMetaAdsPreferences();
  const { data: brand } = useCurrentBrandQuery();
  const [beneficiaryValue, setBeneficiaryValue] = useState(beneficiary ?? '');
  const [payorValue, setPayorValue] = useState(payor ?? '');
  // Flips once the brand types into the beneficiary field, so a brand name
  // that resolves after mount (or a later refetch) never overwrites an edit
  // already in progress.
  const hasEditedBeneficiary = useRef(false);

  useEffect(() => {
    // Only ever suggest the brand name in place of an unset beneficiary: a
    // recorded value or a brand-initiated edit always wins over the suggestion.
    if (beneficiary !== null || hasEditedBeneficiary.current || !brand?.name) {
      return;
    }
    setBeneficiaryValue(brand.name);
  }, [beneficiary, brand?.name]);

  const trimmedBeneficiary = beneficiaryValue.trim();
  const trimmedPayor = payorValue.trim();
  const canSave = trimmedBeneficiary.length > 0 && !savePreferences.isPending;

  const handleSave = () => {
    if (!canSave) {
      return;
    }

    savePreferences.mutate(
      {
        dsaBeneficiary: trimmedBeneficiary,
        // Null clears a payor the brand removed; sending the beneficiary itself
        // would declare a distinct payer that does not exist.
        dsaPayor: trimmedPayor.length > 0 ? trimmedPayor : null,
      },
      {
        onSuccess: () => {
          // The blocker lives on the boost readiness, which this endpoint does
          // not own: re-read it so the form gives way to the launch.
          queryClient.invalidateQueries({ queryKey: mentionBoostSetupQueryKey(mentionId) });
        },
        onError: (error) =>
          toaster.error({
            title: t('common.error'),
            description: getApiErrorMessage(error, t, 'mentions.boost.setup.dsa.error'),
          }),
      }
    );
  };

  return (
    <VStack align="stretch" gap={3}>
      <Text fontSize="xs" color="gray.500" lineHeight="1.5">
        {t('mentions.boost.setup.dsa.description')}
      </Text>

      <Box>
        <SetupFieldLabel>{t('mentions.boost.setup.dsa.beneficiaryLabel')}</SetupFieldLabel>
        <Input
          h={11}
          variant="outline"
          value={beneficiaryValue}
          placeholder={t('mentions.boost.setup.dsa.beneficiaryPlaceholder')}
          onChange={(event) => {
            hasEditedBeneficiary.current = true;
            setBeneficiaryValue(event.target.value);
          }}
        />
      </Box>

      <Box>
        <SetupFieldLabel>{t('mentions.boost.setup.dsa.payorLabel')}</SetupFieldLabel>
        <Input
          h={11}
          variant="outline"
          value={payorValue}
          placeholder={t('mentions.boost.setup.dsa.payorPlaceholder')}
          onChange={(event) => setPayorValue(event.target.value)}
        />
        <Text fontSize="xs" color="gray.500" mt={1.5}>
          {t('mentions.boost.setup.dsa.payorHelper')}
        </Text>
      </Box>

      <Button
        alignSelf="flex-start"
        size="sm"
        variant="outline"
        colorPalette="primary"
        disabled={!canSave}
        loading={savePreferences.isPending}
        onClick={handleSave}
      >
        {t('mentions.boost.setup.dsa.save')}
      </Button>
    </VStack>
  );
}
